<template>
  <aside class="sidebar">
    <h2 class="sidebar__heading">{{ $t("notes.categories") }}</h2>
    <ul class="sidebar__list">
      <li v-for="cat in defaultCategories" :key="cat.key">
        <button
          type="button"
          class="sidebar__item"
          :class="{ 'sidebar__item--active': isCategoryActive(cat.key) }"
          @click="$emit('select-category', cat.key)"
        >
          <img
            class="sidebar__emoji"
            :src="emoji[cat.emoji]"
            alt=""
            aria-hidden="true"
            width="28"
            height="28"
            decoding="async"
          />
          <span class="sidebar__label">{{ $t("notes.category." + cat.key) }}</span>
          <span v-if="categoryCount(cat.key)" class="sidebar__count">{{ countLabel(categoryCount(cat.key)) }}</span>
        </button>
      </li>
    </ul>

    <h2 class="sidebar__heading">{{ $t("notes.folders") }}</h2>
    <ul v-if="folders.length > 0" class="sidebar__list">
      <li
        v-for="folder in folders"
        :key="folder.id"
        class="sidebar__row"
      >
        <button
          type="button"
          class="sidebar__item"
          :class="{ 'sidebar__item--active': isFolderActive(folder.name) }"
          @click="$emit('select-folder', folder.name)"
        >
          <span class="sidebar__tile" :style="{ background: rgbaToCss(folder.color) }" aria-hidden="true">
            <SfIcon v-if="subjectIcon(folder)" :name="subjectIcon(folder)" :size="15" :stroke-width="2.2" />
            <span v-else class="sidebar__initial">{{ folderInitial(folder) }}</span>
          </span>
          <span class="sidebar__label">{{ folder.name }}</span>
          <span v-if="folderCount(folder.name)" class="sidebar__count">{{ countLabel(folderCount(folder.name)) }}</span>
        </button>
        <button
          type="button"
          class="sidebar__row-action"
          :aria-label="$t('notes.folderActions')"
          :title="$t('notes.folderActions')"
          @click.stop="toggleMenu(folder, $event)"
        >
          <SfIcon name="more" :size="16" />
        </button>
      </li>
    </ul>

    <button
      type="button"
      class="sidebar__create"
      @click="onCreateFolder"
    >
      <SfIcon name="folderPlus" :size="16" />
      <span>{{ $t("notes.newFolder") }}</span>
    </button>

    <Teleport to="body">
      <div
        v-if="openMenuFolder"
        class="folder-menu-overlay"
        @click.self="closeMenu"
      >
        <div
          class="folder-menu"
          role="menu"
          :style="{ top: menuPos.top + 'px', left: menuPos.left + 'px' }"
        >
          <button
            type="button"
            class="folder-menu__item"
            role="menuitem"
            @click="onRename(openMenuFolder)"
          >
            <SfIcon name="pencil" :size="15" />
            <span>{{ $t("notes.renameFolder") }}</span>
          </button>
          <button
            type="button"
            class="folder-menu__item"
            role="menuitem"
            @click="openColorPicker(openMenuFolder)"
          >
            <SfIcon name="palette" :size="15" />
            <span>{{ $t("notes.changeFolderColor") }}</span>
          </button>
          <button
            type="button"
            class="folder-menu__item folder-menu__item--danger"
            role="menuitem"
            @click="onDelete(openMenuFolder)"
          >
            <SfIcon name="trash" :size="15" />
            <span>{{ $t("notes.deleteFolder") }}</span>
          </button>
        </div>
      </div>

      <div
        v-if="colorPickerFolder"
        class="folder-menu-overlay"
        @click.self="closeColorPicker"
      >
        <div
          class="folder-color-picker"
          role="dialog"
          aria-modal="true"
          :style="{ top: menuPos.top + 'px', left: menuPos.left + 'px' }"
        >
          <p class="folder-color-picker__title">
            {{ $t("notes.changeFolderColor") }}
          </p>
          <div class="folder-color-picker__swatches">
            <button
              v-for="(color, idx) in palette"
              :key="idx"
              type="button"
              class="folder-color-picker__swatch"
              :style="{ background: rgbaToCss(color) }"
              :aria-label="rgbaToCss(color)"
              @click="onPickColor(color)"
            ></button>
          </div>
        </div>
      </div>
    </Teleport>
  </aside>
</template>

<script>
import {
  DEFAULT_CATEGORIES,
  filterNotes,
  FOLDER_COLOR_PALETTE,
  createFolder,
  renameFolder,
  setFolderColor,
  deleteFolder,
  notesState,
} from "../stores/notesStore";
import { showSnack } from "../stores/snackbarStore";

import SfIcon from "./SfIcon.vue";
import { folderSubjectIcon } from "../utils/folderSubject";
import { EMOJI } from "../assets";

export default {
  name: "FolderSidebar",
  components: { SfIcon },
  props: {
    notes: { type: Array, required: true },
    sharedNotes: { type: Array, default: () => [] },
    folders: { type: Array, required: true },
    activeCategory: { type: String, default: "ALL" },
    activeFolder: { type: String, default: null },
  },
  emits: ["select-category", "select-folder"],
  data() {
    return {
      openMenuFolder: null,
      colorPickerFolder: null,
      menuPos: { top: 0, left: 0 },
      palette: FOLDER_COLOR_PALETTE,
      emoji: EMOJI,
    };
  },
  computed: {
    defaultCategories() {
      return DEFAULT_CATEGORIES;
    },
  },
  methods: {
    // The list loads 25 notes at a time, so a count taken before the last
    // page arrives is a lower bound and says so.
    countLabel(n) {
      return notesState.hasMore ? `${n}+` : String(n);
    },
    categoryCount(key) {
      return filterNotes(this.notes, key, null, this.sharedNotes).length;
    },
    folderCount(name) {
      return this.notes.filter((n) => !n.deleted && n.folder === name).length;
    },
    subjectIcon(folder) {
      return folderSubjectIcon(folder);
    },
    folderInitial(folder) {
      return (folder.name || "?").trim().charAt(0).toUpperCase();
    },
    isCategoryActive(key) {
      return !this.activeFolder && this.activeCategory === key;
    },
    isFolderActive(name) {
      return this.activeFolder === name;
    },
    rgbaToCss(rgba) {
      const r = Math.round((rgba[0] || 0) * 255);
      const g = Math.round((rgba[1] || 0) * 255);
      const b = Math.round((rgba[2] || 0) * 255);
      const a = rgba[3] != null ? rgba[3] : 1;
      return `rgba(${r}, ${g}, ${b}, ${a})`;
    },
    toggleMenu(folder, event) {
      if (this.openMenuFolder && this.openMenuFolder.id === folder.id) {
        this.closeMenu();
        return;
      }
      this.colorPickerFolder = null;
      // Menu is `position: fixed`, so top/left are viewport-relative — do NOT
      // add window.scrollY (that would push it off-screen when scrolled).
      const rect = event.currentTarget.getBoundingClientRect();
      const menuHeight = 150; // approx height for 3 items + padding
      const menuWidth = 220;
      const flipUp = rect.bottom + 6 + menuHeight > window.innerHeight - 8;
      const top = flipUp
        ? Math.max(8, rect.top - 6 - menuHeight)
        : rect.bottom + 6;
      const left = Math.min(
        Math.max(8, rect.right - menuWidth),
        window.innerWidth - menuWidth - 8,
      );
      this.menuPos = { top, left };
      this.openMenuFolder = folder;
    },
    closeMenu() {
      this.openMenuFolder = null;
    },
    closeColorPicker() {
      this.colorPickerFolder = null;
    },
    async onCreateFolder() {
      const name = window.prompt(this.$t("notes.newFolderPrompt"));
      if (!name || !name.trim()) return;
      try {
        await createFolder(name.trim().slice(0, 50));
        showSnack(this.$t("notes.folderCreated"), { variant: "success" });
      } catch (err) {
        console.error("createFolder failed:", err);
        showSnack(this.$t("notes.folderUpdateFailed"), { variant: "error" });
      }
    },
    async onRename(folder) {
      this.closeMenu();
      const next = window.prompt(this.$t("notes.renameFolderPrompt"), folder.name);
      if (next == null) return;
      const trimmed = next.trim().slice(0, 50);
      if (!trimmed || trimmed === folder.name) return;
      try {
        await renameFolder(folder.id, folder.name, trimmed);
        if (this.activeFolder === folder.name) {
          this.$emit("select-folder", trimmed);
        }
        showSnack(this.$t("notes.folderRenamed"), { variant: "success" });
      } catch (err) {
        console.error("renameFolder failed:", err);
        showSnack(this.$t("notes.folderUpdateFailed"), { variant: "error" });
      }
    },
    openColorPicker(folder) {
      this.colorPickerFolder = folder;
      this.openMenuFolder = null;
    },
    async onPickColor(color) {
      const folder = this.colorPickerFolder;
      this.closeColorPicker();
      if (!folder) return;
      try {
        await setFolderColor(folder.id, [...color]);
        showSnack(this.$t("notes.folderColorUpdated"), { variant: "success" });
      } catch (err) {
        console.error("setFolderColor failed:", err);
        showSnack(this.$t("notes.folderUpdateFailed"), { variant: "error" });
      }
    },
    async onDelete(folder) {
      this.closeMenu();
      const ok = window.confirm(this.$t("notes.deleteFolderConfirm"));
      if (!ok) return;
      try {
        await deleteFolder(folder.id, folder.name);
        if (this.activeFolder === folder.name) {
          this.$emit("select-category", "ALL");
        }
        showSnack(this.$t("notes.folderDeleted"), { variant: "success" });
      } catch (err) {
        console.error("deleteFolder failed:", err);
        showSnack(this.$t("notes.folderUpdateFailed"), { variant: "error" });
      }
    },
  },
};
</script>

<style scoped>
/* iOS inset-grouped look: coloured 8px tiles with a white glyph, as on the
   app's settings rows; the selected row takes the yellow tint the app uses
   for a selected note. */
.sidebar {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 10px 18px;
  background: var(--wn-card);
  border-radius: var(--wn-radius);
  align-self: start;
  position: sticky;
  top: 24px;
  max-height: calc(100dvh - 48px);
  overflow-y: auto;
  overscroll-behavior: contain;
}

.sidebar__heading {
  margin: 14px 10px 6px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: var(--wn-secondary);
  text-transform: uppercase;
}

.sidebar__heading:first-child {
  margin-top: 2px;
}

.sidebar__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 1px;
}

.sidebar__row {
  position: relative;
  display: flex;
  align-items: center;
}

.sidebar__row .sidebar__item {
  flex: 1;
  padding-right: 36px;
}

.sidebar__item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 7px 10px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: var(--wn-label);
  font: inherit;
  font-size: 14.5px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.15s ease;
}

.sidebar__item:hover {
  background: var(--wn-fill);
}

.sidebar__item:focus-visible,
.sidebar__row-action:focus-visible,
.sidebar__create:focus-visible {
  outline: 2px solid var(--wn-accent);
  outline-offset: 1px;
}

.sidebar__item--active,
.sidebar__item--active:hover {
  background: var(--wn-accent-soft);
}

.sidebar__tile {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
}

/* The category rows carry the 3D emoji iOS shows on its folder grid. */
.sidebar__emoji {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  object-fit: contain;
}

.sidebar__initial {
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
}

.sidebar__label {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar__count {
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: var(--wn-secondary);
}

.sidebar__row:hover .sidebar__count {
  visibility: hidden;
}

.sidebar__row-action {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 26px;
  height: 26px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--wn-secondary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease, background-color 0.15s ease;
}

.sidebar__row:hover .sidebar__row-action,
.sidebar__row-action:focus-visible {
  opacity: 1;
}

.sidebar__row-action:hover {
  background: var(--wn-fill);
  color: var(--wn-label);
}

.sidebar__create {
  margin: 8px 4px 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 10px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: var(--wn-accent-text);
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.sidebar__create:hover {
  background: var(--wn-fill);
}

@media (max-width: 860px) {
  .sidebar {
    position: static;
    max-height: none;
    overflow: visible;
    padding: 10px;
  }
  .sidebar__heading {
    margin: 8px 6px 4px;
  }
  .sidebar__list {
    display: flex;
    gap: 6px;
    overflow-x: auto;
    padding-bottom: 4px;
    scrollbar-width: none;
  }
  .sidebar__item {
    width: auto;
    white-space: nowrap;
    background: var(--wn-fill);
    padding: 6px 12px 6px 6px;
  }
  .sidebar__row .sidebar__item {
    padding-right: 34px;
  }
  .sidebar__row-action {
    opacity: 1;
  }
}
</style>

<style>
/* Body-teleported menus — escape the sidebar's stacking context. */
.folder-menu-overlay {
  position: fixed;
  inset: 0;
  z-index: 1500;
  background: transparent;
}

.folder-menu,
.folder-color-picker {
  position: fixed;
  min-width: 200px;
  padding: 6px;
  border-radius: 12px;
  background: var(--app-surface-strong, #0b0b0b);
  border: 1px solid var(--app-border, rgba(255, 255, 255, 0.08));
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
}

.folder-menu__item {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 0;
  background: transparent;
  color: var(--app-text, #fff);
  font-size: 13px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  width: 100%;
}

.folder-menu__item + .folder-menu__item {
  margin-top: 2px;
}

.folder-menu__item:hover {
  background: color-mix(in srgb, var(--app-text) 10%, transparent);
}

.folder-menu__item--danger {
  color: #f87171;
}

.folder-color-picker__title {
  margin: 4px 6px 8px;
  font-size: 12px;
  font-weight: 600;
  color: var(--app-text-muted, #9ca3af);
}

.folder-color-picker__swatches {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  padding: 4px;
}

.folder-color-picker__swatch {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  border: 1px solid color-mix(in srgb, var(--app-text) 18%, transparent);
  cursor: pointer;
  padding: 0;
}

.folder-color-picker__swatch:hover {
  transform: scale(1.08);
}
</style>
